import { useState, useEffect } from "react";
import { ModalForm } from "@/components/ModalForm";
import { CompanySearch } from "./CompanySearch";
import { CompanyLogo } from "./CompanyLogo";
import { BasicInformation } from "./BasicInformation";
import { AddressInformation } from "./AddressInformation";
import { useTranslation } from "react-i18next";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { fetchCustomFields, CustomField, CustomFieldsJSON } from "@/integrations/supabase/customFieldsApi";
import { useAuth } from "@/providers/AuthProvider";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Building2, FileText } from "lucide-react";
import { CompanyRelationship } from "@/utils/companyRelationship";

interface AddCompanyFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (company: {
    name: string;
    assignee?: string;
    street?: string;
    postal_code?: string;
    city?: string;
    country?: string;
    vat_number?: string;
    website_url?: string;
    logo_url?: string;
    relationship?: CompanyRelationship;
    custom_fields?: Record<string, any>;
  }) => Promise<boolean>;
  initialCompany?: {
    name: string;
    assignee?: string;
    street?: string;
    postal_code?: string;
    city?: string;
    country?: string;
    vat_number?: string;
    website_url?: string;
    logo_url?: string;
    relationship?: CompanyRelationship;
    custom_fields?: Record<string, any>;
  };
}

export const AddCompanyForm = ({
  isOpen,
  onClose,
  onSubmit,
  initialCompany
}: AddCompanyFormProps) => {
  const { t } = useTranslation();
  const { currentOrganization, userProfile } = useAuth();
  const organizationId = currentOrganization?.id;
  const [fields, setFields] = useState<CustomField[]>([]);
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [fieldsError, setFieldsError] = useState<string | null>(null);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, any>>({});
  const [activeTab, setActiveTab] = useState("general");
  const [newCompany, setNewCompany] = useState<{
    name: string;
    street: string;
    postal_code: string;
    city: string;
    country: string;
    assignee: string;
    vat_number: string;
    website_url: string;
    logo_url: string;
    relationship: CompanyRelationship;
  }>({
    name: "",
    street: "",
    postal_code: "",
    city: "",
    country: "Belgium",
    assignee: "",
    vat_number: "",
    website_url: "",
    logo_url: "",
    relationship: "none"
  });

  useEffect(() => {
    if (!organizationId) return;
    setFieldsLoading(true);
    setFieldsError(null);
    fetchCustomFields(organizationId)
      .then((data: CustomFieldsJSON) => {
        setFields(data.Companies || []);
      })
      .catch(() => {
        setFieldsError("Failed to load custom fields");
      })
      .finally(() => setFieldsLoading(false));
  }, [organizationId]);

  useEffect(() => {
    if (isOpen && userProfile?.id && !newCompany.assignee) {
      setNewCompany((prev) => ({ ...prev, assignee: userProfile.id }));
    }
  }, [isOpen, userProfile?.id]);

  useEffect(() => {
    if (!isOpen || !initialCompany) return;
    setNewCompany((prev) => ({
      ...prev,
      name: initialCompany.name ?? prev.name,
      street: initialCompany.street ?? prev.street,
      postal_code: initialCompany.postal_code ?? prev.postal_code,
      city: initialCompany.city ?? prev.city,
      country: initialCompany.country ?? prev.country,
      vat_number: initialCompany.vat_number ?? prev.vat_number,
      website_url: initialCompany.website_url ?? prev.website_url,
      logo_url: initialCompany.logo_url ?? prev.logo_url,
      assignee: initialCompany.assignee ?? prev.assignee,
      relationship: initialCompany.relationship ?? prev.relationship,
    }));
    if (initialCompany.custom_fields) setCustomFieldValues(initialCompany.custom_fields);
  }, [isOpen, initialCompany]);

  const handleCustomFieldChange = (name: string, value: any) => {
    setCustomFieldValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setNewCompany({...newCompany, [id]: value});
  };

  const handleLogoChange = (logoUrl: string | undefined) => {
    setNewCompany({...newCompany, logo_url: logoUrl || ""});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const companyPayload = {
      ...newCompany,
      custom_fields: fields.length > 0 ? customFieldValues : undefined
    };
    const success = await onSubmit(companyPayload);
    if (success) {
      setNewCompany({
        name: "",
        street: "",
        postal_code: "",
        city: "",
        country: "Belgium",
        assignee: "",
        vat_number: "",
        website_url: "",
        logo_url: "",
        relationship: "none"
      });
    }
  };

  const handleCompanyFound = (company: {
    name: string;
    street?: string;
    postal_code?: string;
    city?: string;
    country?: string;
    vat_number?: string;
    website_url?: string;
    logo_url?: string;
  }) => {
    setNewCompany({
      ...newCompany,
      name: company.name,
      street: company.street || "",
      postal_code: company.postal_code || "",
      city: company.city || "",
      country: company.country || "Belgium",
      vat_number: company.vat_number || "",
      website_url: company.website_url || "",
      logo_url: company.logo_url || ""
    });
  };

  const clearLogoUrl = () => {
    setNewCompany({...newCompany, logo_url: ""});
  };

  return (
    <ModalForm
      title={t("companies.createModal.title")}
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit}
      submitText={t("companies.createModal.submit")}
      cancelText={t("companies.createModal.cancel")}
      submitDisabled={!newCompany.name.trim()}
      className="max-w-5xl"
    >
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="general">
            <Building2 className="h-4 w-4 mr-2" />
            {t("companies.createModal.generalTab")}
          </TabsTrigger>
          {fields.length > 0 && (
            <TabsTrigger value="custom-fields">
              <FileText className="h-4 w-4 mr-2" />
              {t("companies.tabs.customFields")}
            </TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="general">
          <div className="space-y-5">
            <CompanySearch onCompanyFound={handleCompanyFound} />
            <CompanyLogo 
              logoUrl={newCompany.logo_url} 
              companyName={newCompany.name} 
              onClearLogo={clearLogoUrl}
            />
            <BasicInformation
              name={newCompany.name}
              vatNumber={newCompany.vat_number}
              assignee={newCompany.assignee}
              websiteUrl={newCompany.website_url}
              logoUrl={newCompany.logo_url}
              relationship={newCompany.relationship}
              onChange={handleChange}
              onLogoChange={handleLogoChange}
            />
            <AddressInformation
              street={newCompany.street}
              postalCode={newCompany.postal_code}
              city={newCompany.city}
              country={newCompany.country}
              onChange={handleChange}
            />
          </div>
        </TabsContent>
        {fields.length > 0 && (
          <TabsContent value="custom-fields">
            <div className="space-y-4">
              {fieldsLoading && <div>{t("companies.customFields.loading", "Loading custom fields...")}</div>}
              {fieldsError && <div className="text-red-600">{fieldsError}</div>}
              {!fieldsLoading && !fieldsError && fields.length === 0 && (
                <div className="text-gray-500">{t("companies.customFields.noFields", "No custom fields configured.")}</div>
              )}
              {!fieldsLoading && !fieldsError && fields.length > 0 && (
                <form className="space-y-6 pt-2">
                  {fields.map((field) => {
                    const value = customFieldValues[field.name] ?? "";
                    return (
                      <div key={field.name} className="flex flex-col gap-1">
                        <label className="font-medium text-sm mb-1">{field.name}</label>
                        {field.type === "text" && (
                          <Input
                            value={value}
                            onChange={e => handleCustomFieldChange(field.name, e.target.value)}
                            placeholder={field.name}
                            aria-label={field.name}
                          />
                        )}
                        {field.type === "number" && (
                          <Input
                            type="number"
                            value={value}
                            onChange={e => handleCustomFieldChange(field.name, e.target.value)}
                            placeholder={field.name}
                            aria-label={field.name}
                          />
                        )}
                        {field.type === "boolean" && (
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={!!value}
                              onCheckedChange={checked => handleCustomFieldChange(field.name, checked)}
                              id={field.name}
                              aria-label={field.name}
                            />
                            <label htmlFor={field.name} className="text-sm">{field.name}</label>
                          </div>
                        )}
                        {field.type === "dropdown" && field.options && (
                          <Select
                            value={value}
                            onValueChange={val => handleCustomFieldChange(field.name, val)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder={t("companies.customFields.selectOption", { field: field.name })} />
                            </SelectTrigger>
                            <SelectContent>
                              {field.options.map(opt => (
                                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    );
                  })}
                </form>
              )}
            </div>
          </TabsContent>
        )}
      </Tabs>
    </ModalForm>
  );
};
